<template>
  <div class="object-statistic-shows">
    <objects-realty-statistic-summary-section :values="summaryValues" class="object-statistic-shows__summary" />

    <v-chart-with-filters
      class="object-statistic-shows__change"
      type="line"
      :title="changeTitle"
      :datasets="change.datasets"
      :labels="change.labels"
      :titles="change.titles"
      :filters="change.filters"
      with-date-filter
      with-display-market
      :chart-options="{ xAxesType: 'time', yAxesRound: false, yAxesBeginAtZero: false }"
      @change-filters="changeFilters"
    />

    <v-chart-with-filters
      v-if="!isArchive"
      class="object-statistic-shows__similar-graph"
      type="bar"
      :title="similarGraphTitle"
      :datasets="similar.datasets"
      :labels="similar.labels"
      :chart-options="{ yAxesRound: false, xAxesType: 'category', stacked: true, isNotLabelDate: true }"
    />

    <shows-list
      v-if="!hideList"
      class="object-statistic-shows__list"
      :list="shows.list"
      :count="shows.params.count"
      @reload="refetchShowsList"
    />

    <div v-if="!isArchive" class="object-statistic-shows__similar">
      <v-page-subtitle class="object-statistic-shows__similar-title"
        >Объекты, которые участвуют в сравнительном анализе</v-page-subtitle
      >
      <div class="object-statistic-shows__similar-list">
        <objects-realty-list-item
          v-for="object in similar.list"
          :key="object.id"
          v-bind="object"
          is-extended-view
          hide-actions
        />
      </div>
    </div>
  </div>
</template>

<script>
import ObjectsRealtyStatisticSummarySection from '@/components/common/SummarySection.vue'
import ObjectsRealtyListItem from '@/components/ObjectsRealty/ListItem.vue'
import VChartWithFilters from '@/components/common/VChartWithFilters.vue'
import VPageSubtitle from '@/components/common/VPageSubtitle.vue'
import ShowsList from '@/components/Shows/List.vue'
import { advertsService } from '@/services/http'
import { loadingService } from '@/services/loading'
import { formatObjectRealty } from '@/utils/formatters'
import { CUSTOM_DATE_TYPE, SIX_MONTH_TYPE, THIS_MONTH_TYPE } from '@/constants/dateRangeTypes'
import { abbreviatedDate, cloneDeep, createChartDataset } from '@/utils/common'

export default {
  name: 'ObjectsRealtyStatisticShows',
  components: {
    ObjectsRealtyStatisticSummarySection,
    ObjectsRealtyListItem,
    VChartWithFilters,
    VPageSubtitle,
    ShowsList
  },
  props: {
    objectId: { type: [Number, String], required: true },
    isArchive: { type: Boolean, default: false },
    hideList: { type: Boolean, default: false }
  },
  inject: ['mediaQueries', 'graphsMainColor'],
  data() {
    return {
      summary: null,

      change: {
        filters: {
          dateRangeType: SIX_MONTH_TYPE,
          dateFrom: null,
          dateTo: null,
          isDisplayMarket: false
        },
        datasets: [],
        labels: [],
        titles: {
          current: [],
          similar: []
        }
      },
      similar: {
        datasets: [],
        labels: [],
        list: []
      },
      shows: {
        list: [],
        params: {
          limit: 10,
          offset: 0,
          count: 0
        }
      }
    }
  },
  computed: {
    changeTitle() {
      if (this.isArchive) return 'График истории показов'
      return 'График показов'
    },
    similarGraphTitle() {
      if (this.mediaQueries.mobile) return 'Анализ показов среди схожих объектов '
      if (this.mediaQueries.tablet) return 'Анализ показов среди схожих объектов в этом месяце'
      return 'Анализ показов среди схожих объектов (последние 30 дней)'
    },
    summaryValues() {
      if (!this.summary) return []

      const values = [
        { title: 'Кол-во показов', value: this.summary.totalShows },
        { title: 'Средн. в месяц', value: this.summary.averageShows }
      ]

      if (!this.isArchive) {
        values.push({ title: 'Кол-во показов (рынок)', value: this.summary.similarAdvertsTotalShows })
        values.push({ title: 'Средн. в месяц (рынок)', value: this.summary.similarAdvertsAverageShows })
      }

      return values
    }
  },
  created() {
    loadingService.setViewLoading(true)

    const summaryRequest = advertsService.getAdvertShowsSummary(this.objectId).then(data => {
      this.summary = data
    })
    const changeRequest = this.fetchChangeGraph()
    const barGraphRequest = this.fetchBarGraph()
    const listRequest = this.fetchShowsList()
    const listSimilarObjects = this.fetchSimilarObjects()

    Promise.all([summaryRequest, changeRequest, barGraphRequest, listRequest, listSimilarObjects]).finally(() => {
      loadingService.setViewLoading(false)
    })
  },
  methods: {
    fetchChangeGraph() {
      return advertsService.getAdvertShowsChange(this.objectId, this.change.filters).then(data => {
        const datasets = []

        const defaultDataset = createChartDataset({
          data: data.graph,
          color: this.graphsMainColor,
          label: 'Показы объекта',
          filled: true
        })
        datasets.push(defaultDataset)

        if (data.similarGraph) {
          const similarDataset = createChartDataset({
            data: data.similarGraph,
            color: 'green',
            label: 'Показы в среднем по рынку'
          })
          datasets.push(similarDataset)
          this.change.titles.similar = data.similarGraph.map(i => i.date)
        } else {
          this.change.titles.similar = []
        }

        this.change.datasets = datasets
        this.change.labels = data.graph.map(i => i.date)
        this.change.titles.current = data.graph.map(i => i.date)

        if (
          this.change.filters.dateRangeType !== THIS_MONTH_TYPE &&
          this.change.filters.dateRangeType !== CUSTOM_DATE_TYPE
        ) {
          this.change.labels = abbreviatedDate(this.change.labels)
          this.change.titles.current = abbreviatedDate(this.change.titles.current)
          this.change.titles.similar = abbreviatedDate(this.change.titles.similar)
        }
      })
    },
    fetchBarGraph() {
      return advertsService.getAdvertShowsBarGraph(this.objectId).then(data => {
        const sortedDatasets = [...data.similarAdverts, data.advert].sort(
          (a, b) => b.monthShowsCount - a.monthShowsCount
        )
        const length = sortedDatasets.length + 1

        this.similar.datasets = sortedDatasets.map((el, index) => {
          const datalist = Array.from({ length }, (_, i) => (i === index ? el.monthShowsCount : null))
          const isCurrent = Number(el.id) === Number(this.objectId)
          const color = isCurrent ? this.graphsMainColor : 'gray'
          const label = isCurrent ? 'Этот объект' : el.name

          return createChartDataset({ data: datalist, color, label, barThickness: 24 })
        })
        this.similar.labels = sortedDatasets.map(el => `№ ${el.id}`)
      })
    },
    formatShowsList({ list }) {
      return list.map(show => {
        if (show.account) {
          const formatShow = cloneDeep(show)
          const { phone, name } = show.account
          if (phone) formatShow.clientPhone = phone
          if (name) formatShow.clientName = name
          return formatShow
        }
        return show
      })
    },
    fetchShowsList(params) {
      const fetchParams = { ...this.shows.params, ...params, orderBy: '-show_date' }
      return advertsService.getAdvertShowsList(this.objectId, fetchParams).then(({ count, results }) => {
        this.shows.list = this.formatShowsList({ list: results })
        this.shows.params = { ...params, count }
      })
    },
    // ! Временная копипаста
    fetchSimilarObjects() {
      return advertsService.getAdvertPriceSimilar(this.objectId).then(data => {
        this.similar.list = data.similarAdverts.map(el => formatObjectRealty(el))
      })
    },
    refetchShowsList(params) {
      loadingService.setViewLoading(true)
      this.fetchShowsList(params).finally(() => {
        loadingService.setViewLoading(false)
      })
    },
    changeFilters(filters) {
      this.change.filters = filters
      this.fetchChangeGraph()
    }
  }
}
</script>

<style lang="scss">
@import 'src/assets/scss/media';

.object-statistic-shows {
  &__summary {
    margin-bottom: 48px;

    @include tablet {
      margin-bottom: 32px;
    }

    @include phone {
      padding: 0 24px;
    }
  }

  &__change {
    margin-bottom: 48px;

    @include phone {
      margin-bottom: 32px;
    }
  }

  &__similar-title {
    margin-bottom: 16px;

    @include tablet {
      white-space: normal;
    }

    @include phone {
      padding: 0 24px;
      margin-bottom: 24px;
    }
  }

  &__similar-graph {
    margin-bottom: 48px;

    @include tablet {
      margin-bottom: 32px;
    }
  }

  &__list {
    margin-bottom: 48px;

    @include tablet {
      margin-bottom: 32px;
    }
  }
}
</style>
